import React from 'react';
import HelpBadge from './components/HelpBadge';
import type { OfacLanguage } from './ofacIntakeCatalog';
import {
  RESEARCH_PHONE_COUNTRY_OPTIONS,
  RESEARCH_PHONE_OTHER,
  formatResearchPhonesCsv,
  parseResearchPhonesCsv,
  type ResearchPhoneRow,
} from './researchPhoneIntake';

type ResearchPhoneListProps = {
  language: OfacLanguage;
  value: string;
  onChange: (csv: string) => void;
  fieldProps?: (field: string) => Record<string, unknown>;
};

export function ResearchPhoneList({
  language,
  value,
  onChange,
  fieldProps,
}: ResearchPhoneListProps) {
  const spanish = language === 'es';
  const [rows, setRows] = React.useState<ResearchPhoneRow[]>(() => parseResearchPhonesCsv(value));

  React.useEffect(() => {
    setRows(parseResearchPhonesCsv(value));
  }, [value]);

  const commit = (nextRows: ResearchPhoneRow[]) => {
    setRows(nextRows);
    onChange(formatResearchPhonesCsv(nextRows));
  };

  const patchRow = (index: number, patch: Partial<ResearchPhoneRow>) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch, rawE164: undefined } : row));
    commit(next);
  };

  const addRow = () => {
    commit([...rows, { country: 'US', nationalNumber: '' }]);
  };

  const removeRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    commit(next.length ? next : [{ country: 'US', nationalNumber: '' }]);
  };

  return (
    <div className="research-phone-list">
      <div className="research-form-panel-label">
        <span>
          {spanish ? 'Teléfonos' : 'Phone numbers'}
          {' '}
          <HelpBadge
            label={spanish ? 'Ayuda sobre teléfonos internacionales' : 'Help about international phones'}
          >
            {spanish
              ? 'Incluye el código de país. Se guarda como +CC y número (E.164).'
              : 'Include country code. Stored as +CC number (E.164).'}
          </HelpBadge>
        </span>
      </div>
      {rows.map((row, index) => {
        const isOther = row.country === RESEARCH_PHONE_OTHER;
        return (
          <div className="research-intake-pair research-phone-list__row" key={`phone-row-${index}`}>
            <label className="research-form-panel-label">
              <span className="visually-hidden">
                {spanish ? `País del teléfono ${index + 1}` : `Phone country ${index + 1}`}
              </span>
              <select
                aria-label={spanish ? `País del teléfono ${index + 1}` : `Phone country ${index + 1}`}
                value={row.country}
                onChange={(event) => patchRow(index, { country: event.target.value })}
              >
                {RESEARCH_PHONE_COUNTRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {spanish ? option.labelEs : option.labelEn}
                  </option>
                ))}
              </select>
            </label>
            <label className="research-form-panel-label">
              <span className="visually-hidden">
                {spanish ? `Número ${index + 1}` : `Number ${index + 1}`}
              </span>
              <input
                type="tel"
                inputMode="tel"
                {...(fieldProps ? fieldProps(`phones-${index}`) : {})}
                placeholder={isOther
                  ? (spanish ? '+CC número' : '+CC number')
                  : (spanish ? 'Número sin código' : 'Number without country code')}
                value={isOther ? (row.rawE164 || row.nationalNumber) : row.nationalNumber}
                onChange={(event) => {
                  if (isOther) {
                    patchRow(index, { nationalNumber: event.target.value, rawE164: event.target.value });
                  } else {
                    patchRow(index, { nationalNumber: event.target.value });
                  }
                }}
              />
            </label>
            {rows.length > 1 ? (
              <button
                type="button"
                className="dashboard-security-inline-btn"
                onClick={() => removeRow(index)}
              >
                {spanish ? 'Quitar' : 'Remove'}
              </button>
            ) : null}
          </div>
        );
      })}
      <button type="button" className="dashboard-security-inline-btn" onClick={addRow}>
        {spanish ? 'Añadir otro teléfono' : 'Add another phone'}
      </button>
    </div>
  );
}
